<script setup lang="ts">
import { IS_DEVNET } from '@shared/utils/constant'
import { LABS_ALLOWED_ADDRESSES } from '@/app/data/labs'
import {
  isDebugAddressAllowed,
  parseDebugAllowedAddresses
} from '@/app/providers/labs/access'
import { MainPage, TradeSubPage } from '@/types/page'
import { CommonCyTags, DefaultMarket } from '@/types/enums'
import type { NavigationMenuItem } from '@nuxt/ui'

const route = useRoute()
const sharedWalletStore = useSharedWalletStore()
const { t } = useLang()

const navigationMenuUi = {
  childLinkLabel: 'capitalize'
}

const allowedDebugAddresses = computed(() => {
  return parseDebugAllowedAddresses(LABS_ALLOWED_ADDRESSES)
})

const isLabsNavigationVisible = computed(() => {
  return [sharedWalletStore.injectiveAddress, sharedWalletStore.address].some(
    (address) =>
      isDebugAddressAllowed({
        address,
        allowedAddresses: allowedDebugAddresses.value
      })
  )
})

const menuItems = computed<NavigationMenuItem[]>(() => {
  const items: NavigationMenuItem[] = [
    {
      label: t('navigation.trade'),
      to: {
        name: TradeSubPage.Trade,
        params: { slug: DefaultMarket.Perpetual }
      },
      prefetch: false,
      active: route.path.includes('/trade')
    },
    {
      label: t('navigation.markets'),
      to: { name: MainPage.Markets },
      prefetch: false
    },
    {
      label: t('navigation.predictions'),
      to: { name: MainPage.Predictions },
      prefetch: false,
      active: route.path.startsWith('/predictions')
    },
    {
      to: { name: MainPage.Referral },
      prefetch: false,
      label: t('navigation.more.referral')
    },
    {
      to: { name: MainPage.Portfolio },
      prefetch: false,
      label: t('navigation.portfolio')
    }
  ]

  if (IS_DEVNET) {
    items.push({
      label: t('navigation.points'),
      to: { name: MainPage.Points },
      prefetch: false
    })
  }

  if (isLabsNavigationVisible.value) {
    items.push({
      to: '/labs',
      external: true,
      target: '_blank',
      prefetch: false,
      label: t('navigation.labs'),
      active: route.path.startsWith('/labs')
    })
  }

  items.push({
    label: t('common.more'),
    ui: {
      content: 'w-32',
      childLinkLabel: 'capitalize'
    },
    children: [
      {
        to: 'https://docs.tc.xyz/',
        label: t('common.docs'),
        target: '_blank'
      },
      {
        to: 'https://discord.com/invite/truecurrent',
        label: t('common.support'),
        target: '_blank'
      }
    ]
  })

  return items
})
</script>

<template>
  <UHeader
    v-bind="{
      title: 'TrueCurrent'
    }"
  >
    <template #left>
      <NuxtLink
        to="/"
        :data-cy="dataCyTag(CommonCyTags.HeaderLogo)"
        class="flex items-center gap-3 focus-visible:outline-none"
      >
        <AssetLogo class="h-5.5 w-auto text-primary" />
      </NuxtLink>

      <UNavigationMenu
        highlight
        :items="menuItems"
        :external-icon="false"
        :data-cy="dataCyTag(CommonCyTags.HeaderNavigation)"
        content-orientation="vertical"
        class="max-lg:hidden gap-4"
        :ui="navigationMenuUi"
      >
        <template #item-label="{ item }">
          <span
            class="capitalize"
            :data-cy="dataCyTag(CommonCyTags.HeaderNavigationItem)"
          >
            {{ item.label }}
          </span>
        </template>
      </UNavigationMenu>
    </template>

    <template #right>
      <div :data-cy="dataCyTag(CommonCyTags.HeaderWallet)">
        <LayoutWallet />
      </div>
    </template>

    <template #body>
      <UNavigationMenu
        :items="menuItems"
        :external-icon="false"
        :data-cy="dataCyTag(CommonCyTags.HeaderNavigation)"
        orientation="vertical"
        class="-mx-2.5"
        :ui="navigationMenuUi"
      >
        <template #item-label="{ item }">
          <span
            class="capitalize"
            :data-cy="dataCyTag(CommonCyTags.HeaderNavigationItem)"
          >
            {{ item.label }}
          </span>
        </template>
      </UNavigationMenu>

      <LayoutWalletDetailsCta
        v-if="!IS_DEVNET && sharedWalletStore.isUserConnected"
        class="lg:hidden mt-2"
      />
    </template>
  </UHeader>
</template>
